import React from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, ArrowForward, Delete } from "@mui/icons-material";
import MainButton from "../buttons/mainbutton";
import { File } from "src/model_types/file.types";
import {
  useDeletePhotoMutation,
  useMakeMainPhotoMutation,
} from "src/redux/service/photo.service";
import { useSnackbar } from "notistack";
import { textStyle } from "src/styles/textstyle";

interface ImageModalProps {
  currentImageIndex: number;
  files: File[];
  homeId?: string; // Implying own house
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  open: boolean;
}

const ImagesModal: React.FC<ImageModalProps> = ({
  currentImageIndex,
  files,
  homeId,
  onClose,
  onNext,
  onPrev,
  open,
}) => {
  const [deletePhoto] = useDeletePhotoMutation();
  const [makeMainPhoto] = useMakeMainPhotoMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleDelete = async () => {
    if (!files[currentImageIndex]) return;
    
    try {
      await deletePhoto({
        homeId: homeId!,
        photoId: files[currentImageIndex].fileName!,
      });
      enqueueSnackbar("Deleted photo", {
        variant: "success",
      });
      // Handle success (e.g., show a success message, update state, etc.)
    } catch (error) {
      enqueueSnackbar("Deletion failed. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleMain = async () => {
    if (!files[currentImageIndex]) return;
    
    try {
      await makeMainPhoto({
        homeId: homeId!,
        fileName: files[currentImageIndex].fileName!,
      });
      enqueueSnackbar("Main photo updated", {
        variant: "success",
      });
      // Handle success (e.g., show a success message, update state, etc.)
    } catch (error) {
      enqueueSnackbar("Main photo update failed. Please try again.", {
        variant: "error",
      });
    }
  };

  // Guard against invalid state
  if (!files || files.length === 0 || !files[currentImageIndex]) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography>No images available</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent style={{ position: "relative" }}>
        <IconButton
          style={{ position: "absolute", top: "50%", left: -10 }}
          onClick={onPrev}
        >
          <ArrowBack />
        </IconButton>
        <img
          src={files[currentImageIndex].downloadUrl || ""}
          alt={`Image ${currentImageIndex + 1}`}
          style={{ width: "100%", height: "auto", objectFit: "contain" }}
        />
        <IconButton
          style={{ position: "absolute", top: "50%", right: -10 }}
          onClick={onNext}
        >
          <ArrowForward />
        </IconButton>
        {homeId && (
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            style={{ bottom: 10, right: 10 }}
          >
            <Grid item>
              <IconButton
                onClick={async () => {
                  await handleDelete();
                  onClose();
                }}
                disabled={currentImageIndex === 0}
              >
                <Delete
                  style={{
                    fontSize: 30,
                    color: currentImageIndex === 0 ? "grey" : "black",
                  }}
                />
              </IconButton>
            </Grid>
            <Grid item>
              <MainButton
                text={"Make main picture"}
                disabled={currentImageIndex === 0}
                onClick={() => {
                  handleMain();
                  onClose();
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagesModal;
